import { toast } from "react-toastify";
import { EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { Form, Modal, Space, Switch, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BillingService } from "../../../api/billingService";
import { CountryPricingConfig, CountryPricingConfigRequest } from "../../../@type/billing.types";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import InputField from "../../../components/common/fields/InputField";
import SelectField from "../../../components/common/fields/SelectField";
import StatusBadge from "../../../components/common/StatusBadge";
import MuiTable from "../../../components/common/MuiTable";
import Card from "../../../components/common/Card";
import { useCountries } from "../../../hooks/useCountries";
import { Column } from "../../../utils/constants";

const unwrap = (response: any) => response?.data?.data ?? response?.data ?? [];

const defaultFormValues: CountryPricingConfigRequest = {
  country_code: "",
  booking_commission_percent: 0,
  is_active: true,
};

const BillingFeeConfig = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm<CountryPricingConfigRequest>();
  const [configs, setConfigs] = useState<CountryPricingConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [{ data: countries }] = useCountries();

  const countryOptions = useMemo(() => {
    const options = countries
      .filter((country) => country.code)
      .map((country) => ({
        label: `${country.name} (${country.code})`,
        value: country.code.toUpperCase(),
      }));

    return [
      ...options,
      { label: t("billing.fee.global_fallback"), value: "GLOBAL" },
    ];
  }, [countries, t]);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await BillingService.getCountryPricingConfigs();
      setConfigs(unwrap(response));
    } catch (error) {
      toast.error(t("billing.fee.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const openCreate = () => {
    form.setFieldsValue(defaultFormValues);
    setModalOpen(true);
  };

  const openEdit = (config: CountryPricingConfig) => {
    form.setFieldsValue({
      country_code: config.country_code,
      booking_commission_percent: config.booking_commission_percent,
      is_active: config.is_active,
    });
    setModalOpen(true);
  };

  const saveConfig = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await BillingService.upsertCountryPricingConfig({
        ...values,
        country_code: values.country_code.toUpperCase(),
        booking_commission_percent: Number(values.booking_commission_percent || 0),
        is_active: Boolean(values.is_active),
      });
      toast.success(t("billing.fee.saved"));
      setModalOpen(false);
      await loadConfigs();
    } catch (error: any) {
      if (!error?.errorFields) {
        toast.error(t("billing.fee.save_error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const header: Column[] = [
    {
      id: "country_name",
      label: t("billing.fee.country"),
      minWidth: 160,
      align: "left",
      format: (_: string, row: CountryPricingConfig) => (
        <Space direction="vertical" size={0}>
          <span className="font-semibold">{row.country_name}</span>
          <Typography.Text type="secondary">{row.country_code}</Typography.Text>
        </Space>
      ),
    },
    {
      id: "currency",
      label: t("billing.fee.currency"),
      minWidth: 100,
      align: "center",
      format: (value: string, row: CountryPricingConfig) => `${row.currency_symbol || ""} ${value}`,
    },
    {
      id: "booking_commission_percent",
      label: t("billing.fee.commission"),
      minWidth: 110,
      align: "right",
      format: (value: number) => `${Number(value || 0).toFixed(2)}%`,
    },
    {
      id: "is_active",
      label: t("billing.fee.status"),
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (
        <StatusBadge label={value ? t("billing.fee.active") : t("billing.fee.inactive")} tone={value ? "success" : "error"} />
      ),
    },
    {
      id: "id",
      label: t("billing.fee.actions"),
      minWidth: 90,
      align: "center",
      format: (_: number, row: CountryPricingConfig) => (
        <IconButton size="small" color="success" onClick={() => openEdit(row)}>
          <EditOutlined />
        </IconButton>
      ),
    },
  ];

  return (
    <Card className="user">
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t("billing.fee.title")}
          </Typography.Title>
          <Space>
            <CancelButton startIcon={<ReloadOutlined />} onClick={loadConfigs} loading={loading}>
              {t("billing.fee.reload")}
            </CancelButton>
            <ActionButton startIcon={<PlusOutlined />} onClick={openCreate}>
              {t("billing.fee.add")}
            </ActionButton>
          </Space>
        </Box>

        <MuiTable header={header} data={configs} isLoading={loading} />
      </Space>

      <Modal
        title={t("billing.fee.title")}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={
          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <CancelButton onClick={() => setModalOpen(false)}>{t("btn.cancel")}</CancelButton>
            <ActionButton onClick={saveConfig} loading={saving}>
              {t("billing.fee.save")}
            </ActionButton>
          </Box>
        }
      >
        <Form form={form} layout="vertical" initialValues={defaultFormValues}>
          <Form.Item
            name="country_code"
            label={t("billing.fee.country")}
            rules={[{ required: true, message: t("billing.fee.country_required") }]}
          >
            <SelectField options={countryOptions} placeholder={t("billing.fee.select_country")} />
          </Form.Item>

          <Form.Item
            name="booking_commission_percent"
            label={t("billing.fee.commission_label")}
            rules={[
              { required: true, message: t("billing.fee.commission_required") },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") return Promise.resolve();
                  const num = Number(value);
                  return num >= 0 && num <= 100
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("billing.fee.commission_range")));
                },
              },
            ]}
          >
            <InputField
              type="number"
              inputProps={{ min: 0, max: 100, step: "0.01" }}
              endAdornment={<InputAdornment position="end">%</InputAdornment>}
            />
          </Form.Item>

          <Form.Item name="is_active" label={t("billing.fee.active_label")} valuePropName="checked">
            <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default BillingFeeConfig;
